/**
 * Hook to fetch experience chapter page data via RPC get_experience_page.
 * Returns featured places (curated), near places (auto), and events.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ExperiencePlace {
  id: string;
  slug: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
  descriptionShort: string | null;
  lat: number | null;
  lng: number | null;
  tags: string[];
}

export interface ExperienceEvent {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  timingMode: string;
  outingTags: string[];
  producerName: string | null;
  eventType: string | null;
  customAddress: string | null;
  useProducerAddress: boolean;
  producerCity: string | null;
  areaName: string | null;
}

export interface ExperiencePageData {
  experience: {
    slug: string;
    title: string;
    intro: string;
    hero_media_asset_id: string | null;
    match_rules: Record<string, unknown>;
  } | null;
  featuredPlaces: ExperiencePlace[];
  nearPlaces: ExperiencePlace[];
  events: ExperienceEvent[];
}

function mapPlace(row: any): ExperiencePlace {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    municipality: row.municipality,
    landskap: row.landskap,
    descriptionShort: row.description_short,
    lat: row.lat,
    lng: row.lng,
    tags: row.tags ?? [],
  };
}

function mapEvent(row: any): ExperienceEvent {
  return {
    id: row.id,
    title: row.title,
    startDate: row.start_date,
    endDate: row.end_date,
    timingMode: row.timing_mode,
    outingTags: row.outing_tags ?? [],
    producerName: row.producer_name ?? null,
    eventType: row.event_type ?? null,
    customAddress: row.custom_address ?? null,
    useProducerAddress: row.use_producer_address ?? true,
    producerCity: row.producer_city ?? null,
    areaName: row.area_name ?? null,
  };
}

export function useExperienceData(slug: string | undefined, lat?: number, lng?: number) {
  return useQuery({
    queryKey: ['experience-page', slug, lat, lng],
    queryFn: async (): Promise<ExperiencePageData> => {
      if (!slug) throw new Error('No slug');

      const { data, error } = await supabase.rpc('get_experience_page', {
        p_slug: slug,
        p_lat: lat ?? null,
        p_lng: lng ?? null,
      });

      if (error) throw error;
      if (!data) {
        return { experience: null, featuredPlaces: [], nearPlaces: [], events: [] };
      }

      const result = data as any;
      return {
        experience: result.experience ?? null,
        featuredPlaces: (result.featured_places ?? []).map(mapPlace),
        nearPlaces: (result.near_places ?? []).map(mapPlace),
        events: (result.events ?? []).map(mapEvent),
      };
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}
